import { createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

type ThemeMode = "light" | "dark";

const paletteTokens = {
  light: {
    primary: "#006a8c",
    primaryLight: "#00bbff",
    primaryDark: "#006a8c",
    secondary: "#f9fbfc",
    secondaryLight: "#eff2f4",
    secondaryDark: "#e7e9ea",
    secondaryContrast: "#1a1d1e",
    backgroundDefault: "#fbfcfd",
    backgroundPaper: "#f9fbfc",
    textPrimary: "#1a1d1e",
    textSecondary: "#44515b",
  },
  dark: {
    primary: "#586d7f",
    primaryLight: "#829cb2",
    primaryDark: "#4e5e6b",
    secondary: "#4c5c6a",
    secondaryLight: "#4e5e6b",
    secondaryDark: "#4e5e6b",
    secondaryContrast: "#fcfeff",
    backgroundDefault: "#1a1d1e",
    backgroundPaper: "#30363c",
    textPrimary: "#f7f9fa",
    textSecondary: "#e7e9ea",
  },
} as const;

export const getAppShellBackground = (theme: Theme) => theme.palette.background.default;

export const getContainedButtonDisabledStyles = (theme: Theme) => ({
  color: theme.palette.text.disabled,
  backgroundColor: theme.palette.action.disabledBackground,
  borderColor: "transparent",
});

export const createReconTheme = (mode: ThemeMode) => {
  const tokens = paletteTokens[mode];

  return createTheme({
    shape: {
      borderRadius: 4,
    },
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        light: tokens.primaryLight,
        dark: tokens.primaryDark,
        contrastText: "#ffffff",
      },
      secondary: {
        main: tokens.secondary,
        light: tokens.secondaryLight,
        dark: tokens.secondaryDark,
        contrastText: tokens.secondaryContrast,
      },
      background: {
        default: tokens.backgroundDefault,
        paper: tokens.backgroundPaper,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      // Divider represents the Theme Pack Border.Light token. Use the default
      // border token only for controls that explicitly require it.
      divider: mode === "dark" ? "#44515b" : "#cfd2d5",
      action: {
        hover: mode === "dark" ? "#4e5e6b" : "#e6ebf0",
        selected: mode === "dark" ? "#44515b" : "#e7e9ea",
        disabled: mode === "dark" ? "#929fac" : "#556779",
        disabledBackground: mode === "dark" ? "#44515b" : "#b8c3cc",
      },
      error: {
        main: mode === "dark" ? "#ea8194" : "#bf4d63",
        dark: "#8f2e44",
        light: "#fabcc8",
      },
      warning: {
        main: "#fbb234",
        dark: "#8a5a00",
        light: "#fff4d6",
      },
      success: {
        main: "#07bb43",
        dark: "#0a7a32",
        light: "#dff5e6",
      },
      info: {
        main: "#00bbff",
        dark: "#006a8c",
        light: "#92e2ff",
      },
    },
    typography: {
      fontFamily: '"TT Hoves Pro", sans-serif',
      fontSize: 16,
      h1: {
        fontSize: "2.1875rem",
        lineHeight: "3.25rem",
        fontWeight: 400,
        letterSpacing: 0,
      },
      h2: {
        fontSize: "1.875rem",
        lineHeight: "2.8125rem",
        fontWeight: 400,
        letterSpacing: 0,
      },
      h3: {
        fontSize: "1.5625rem",
        lineHeight: "2.3125rem",
        fontWeight: 400,
        letterSpacing: 0,
      },
      h4: {
        fontSize: "1.125rem",
        lineHeight: "1.5rem",
        fontWeight: 400,
        letterSpacing: 0,
      },
      subtitle1: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
        fontWeight: 600,
        letterSpacing: 0,
      },
      subtitle2: {
        fontSize: "0.875rem",
        lineHeight: "1.3125rem",
        fontWeight: 600,
        letterSpacing: "0.0125rem",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
        fontWeight: 400,
        letterSpacing: "0.03125rem",
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: "1.3125rem",
        fontWeight: 400,
        letterSpacing: "0.015625rem",
      },
      button: {
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: "1rem",
        letterSpacing: "0.07rem",
        textTransform: "uppercase",
      },
      caption: {
        fontFamily: '"TT Hoves Pro", sans-serif',
        fontWeight: 500,
        fontSize: "0.75rem",
        lineHeight: "0.875rem",
        letterSpacing: "0.025rem",
      },
      overline: {
        fontFamily: '"TT Interphases Pro", monospace',
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "0.875rem",
        letterSpacing: "0.025rem",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "::-webkit-scrollbar-thumb": {
            borderRadius: 3,
            backgroundColor: "var(--universal-grey-dark)",
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: "var(--universal-grey-dark) transparent",
          },
          ":root": {
            "--universal-black": "#0d0d0d",
            "--universal-white": "#fcfeff",
            "--universal-grey-light": "#e7e9ea",
            "--universal-grey-dark": "#556779",
            "--surface-background": tokens.backgroundDefault,
            "--surface-default": tokens.backgroundPaper,
            "--surface-muted": mode === "dark" ? "#3b464f" : "#f7f9fa",
            "--surface-soft-alt": mode === "dark" ? "#4c5c6a" : "#eff2f4",
            "--surface-alt": mode === "dark" ? "#44515b" : "#e7e9ea",
            "--surface-elevated": mode === "dark" ? "#929fac" : "#f7f9fa",
            "--surface-hover": mode === "dark" ? "#4e5e6b" : "#e6ebf0",
            "--text-primary": tokens.textPrimary,
            "--text-secondary": tokens.textSecondary,
            "--text-unavailable": mode === "dark" ? "#929fac" : "#556779",
            "--text-link": mode === "dark" ? "#8fe1ff" : "#007ba8",
            "--text-on-primary": "#ffffff",
            "--text-contrast": mode === "dark" ? "#fcfeff" : "#e7e9ea",
            "--text-error": mode === "dark" ? "#ea8194" : "#8f2e44",
            "--border-default": mode === "dark" ? "#b8c3cc" : "#1a1d1e",
            "--border-light": mode === "dark" ? "#44515b" : "#cfd2d5",
            "--border-input": mode === "dark" ? "#b8c3cc" : "#4c5c6a",
            "--border-focus": mode === "dark" ? "#9bb0c1" : "#829cb2",
            "--button-enabled": tokens.primary,
            "--button-hover-focus": tokens.primaryLight,
            "--button-pressed": tokens.primaryDark,
            "--button-unavailable": mode === "dark" ? "#44515b" : "#b8c3cc",
            "--secondary-button-enabled": tokens.secondary,
            "--secondary-button-hover-focus": tokens.secondaryLight,
            "--secondary-button-pressed": tokens.secondaryDark,
            "--secondary-button-unavailable": mode === "dark" ? "#30363c" : "#f7f9fa",
            "--secondary-button-border": mode === "dark" ? "#b8c3cc" : "#4c5c6a",
            "--system-error": mode === "dark" ? "#ea8194" : "#bf4d63",
            "--system-error-subtle": mode === "dark" ? "#8f2e44" : "#fabcc8",
            "--system-success": "#07bb43",
            "--system-success-subtle": mode === "dark" ? "#0a7a32" : "#dff5e6",
            "--system-warning": "#fbb234",
            "--system-warning-subtle": mode === "dark" ? "#8a5a00" : "#fff4d6",
            "--system-info": "#00bbff",
            "--system-info-subtle": mode === "dark" ? "#006a8c" : "#92e2ff",
            "--overlay-scrim": "#556779",
            "--button-height": "36px",
            "--button-horizontal-padding": "16px",
            "--button-vertical-padding": "10px",
            "--button-min-horizontal-padding": "8px",
            "--button-icon-gap": "8px",
            "--button-icon-size": "20px",
            "--button-loading-spinner-size": "16px",
            "--button-radius": "8px",
            "--field-control-height": "46px",
            "--field-radius": "4px",
            "--field-padding-x": "12px",
            "--field-padding-y": "11px",
            "--field-textarea-min-height": "80px",
            "--filter-chip-min-horizontal-padding": "8px",
            "--filter-chip-preferred-horizontal-padding": "12px",
            "--filter-chip-gap": "8px",
            "--filter-chip-menu-width": "240px",
            "--filter-chip-menu-max-height": "380px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 4,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paperAnchorLeft: {
            marginTop: 0,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            height: "var(--button-height)",
            minHeight: "var(--button-height)",
            minWidth: 0,
            padding: "var(--button-vertical-padding) var(--button-horizontal-padding)",
            borderRadius: "var(--button-radius)",
            fontFamily: '"TT Hoves Pro", sans-serif',
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: "1rem",
            letterSpacing: "0.07rem",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            "& .MuiButton-startIcon": {
              marginLeft: 0,
              marginRight: "var(--button-icon-gap)",
              "& .MuiSvgIcon-root": { fontSize: "var(--button-icon-size)" },
            },
            "& .MuiButton-endIcon": {
              marginLeft: "var(--button-icon-gap)",
              marginRight: 0,
              "& .MuiSvgIcon-root": { fontSize: "var(--button-icon-size)" },
            },
            "& .MuiCircularProgress-root": {
              width: "var(--button-loading-spinner-size) !important",
              height: "var(--button-loading-spinner-size) !important",
            },
            "&.Mui-focusVisible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              cursor: "not-allowed",
            },
          }),
          containedPrimary: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.light },
            "&:active": { backgroundColor: theme.palette.primary.dark },
            "&.Mui-focusVisible": {
              backgroundColor: theme.palette.primary.light,
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              boxShadow: `inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
            "&.Mui-disabled": getContainedButtonDisabledStyles(theme),
          }),
          containedSecondary: ({ theme }) => ({
            color: theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            border: "1px solid var(--secondary-button-border)",
            boxShadow: theme.shadows[1],
            "&:hover": { backgroundColor: theme.palette.secondary.light },
            "&:active": { backgroundColor: theme.palette.secondary.dark },
            "&.Mui-focusVisible": {
              backgroundColor: theme.palette.secondary.light,
              outline: `2px solid ${theme.palette.primary.light}`,
              outlineOffset: 2,
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              backgroundColor: "var(--secondary-button-unavailable)",
              borderColor: "transparent",
              boxShadow: "none",
            },
          }),
          outlinedSecondary: ({ theme }) => ({
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
            backgroundColor: "transparent",
            "&:hover": { borderColor: theme.palette.text.secondary, backgroundColor: theme.palette.action.hover },
            "&:active": { backgroundColor: theme.palette.action.selected },
            "&.Mui-focusVisible": {
              borderColor: "var(--border-default)",
              outline: `2px solid ${theme.palette.primary.light}`,
              outlineOffset: 2,
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              borderColor: "var(--secondary-button-unavailable)",
              backgroundColor: "var(--secondary-button-unavailable)",
            },
          }),
          outlinedPrimary: ({ theme }) => ({
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
            backgroundColor: "transparent",
            "&:hover": { borderColor: theme.palette.text.secondary, backgroundColor: theme.palette.action.hover },
            "&:active": { backgroundColor: theme.palette.action.selected },
            "&.Mui-focusVisible": {
              borderColor: "var(--border-default)",
              outline: `2px solid ${theme.palette.primary.light}`,
              outlineOffset: 2,
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              borderColor: "var(--secondary-button-unavailable)",
              backgroundColor: "var(--secondary-button-unavailable)",
            },
          }),
          textSecondary: ({ theme }) => ({
            color: theme.palette.text.primary,
            "&:hover": { backgroundColor: theme.palette.action.hover },
            "&:active": { backgroundColor: theme.palette.action.selected },
            "&.Mui-focusVisible": {
              backgroundColor: theme.palette.secondary.light,
              outline: `2px solid ${theme.palette.primary.light}`,
              outlineOffset: 2,
            },
            "&.Mui-disabled": { backgroundColor: "transparent" },
          }),
          textPrimary: ({ theme }) => ({
            color: theme.palette.text.primary,
            "&:hover": { backgroundColor: theme.palette.action.hover },
            "&:active": { backgroundColor: theme.palette.action.selected },
            "&.Mui-focusVisible": {
              backgroundColor: theme.palette.primary.light,
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              boxShadow: `inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
            "&.Mui-disabled": { backgroundColor: "transparent" },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: "var(--button-height)",
            height: "var(--button-height)",
            minWidth: "var(--button-height)",
            minHeight: "var(--button-height)",
            padding: 8,
            borderRadius: "var(--button-radius)",
            "& .MuiSvgIcon-root": { fontSize: "var(--button-icon-size)" },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: "var(--field-control-height)",
            borderRadius: "var(--field-radius)",
            backgroundColor: theme.palette.background.paper,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--border-input)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--border-default)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--border-focus)",
              borderWidth: 2,
            },
            // Theme Pack fields use an inside label, not MUI's outlined-field notch.
            "& .MuiOutlinedInput-notchedOutline legend": { maxWidth: "0.01px !important", visibility: "hidden" },
          }),
          input: {
            padding: "10px var(--field-padding-x)",
            fontSize: "1rem",
            lineHeight: "1.5rem",
          },
          multiline: {
            minHeight: "var(--field-textarea-min-height)",
            alignItems: "flex-start",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            top: 0,
            left: 0,
            transform: "translate(12px, 11px)",
            fontSize: "1rem",
            lineHeight: "1.5rem",
            color: "var(--text-primary)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(12px, -8px)",
              fontSize: "0.75rem",
              lineHeight: "1rem",
              color: "var(--text-secondary)",
              padding: "0 4px",
              backgroundColor: "var(--surface-background)",
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: "1rem",
            lineHeight: "1.5rem",
            letterSpacing: "0.03125rem",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: "8px 16px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            letterSpacing: "0.03125rem",
          }),
          head: ({ theme }) => ({
            height: 56,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            fontWeight: 600,
            textTransform: "uppercase",
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: 40,
            backgroundColor: theme.palette.background.paper,
            "&.MuiTableRow-hover:hover": { backgroundColor: theme.palette.action.hover },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "none",
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            overflowX: "auto",
            overflowY: "auto",
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 4,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: "24px 16px" } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { minHeight: 52, padding: "8px 16px", gap: 18 } },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 4, alignItems: "center" },
          message: { fontSize: "1rem", lineHeight: "1.5rem" },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 48 }),
          indicator: ({ theme }) => ({ height: 2, backgroundColor: theme.palette.primary.main }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 48,
            padding: "10px 24px",
            fontSize: "0.875rem",
            lineHeight: "1rem",
            fontWeight: 500,
            letterSpacing: "0.07rem",
            color: theme.palette.text.secondary,
            "&.Mui-selected": { color: theme.palette.primary.main },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: "var(--button-radius)",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
            maxHeight: "var(--filter-chip-menu-max-height)",
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 40,
            padding: "8px 16px",
            fontSize: "1rem",
            lineHeight: "1.5rem",
            backgroundColor: theme.palette.background.paper,
            "&:hover": { backgroundColor: theme.palette.action.hover },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 52,
            padding: "8px 16px",
            gap: 16,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            "&:hover": { backgroundColor: theme.palette.action.hover },
            "&.Mui-selected, &.Mui-selected:hover": {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.hover,
            },
            "&.Mui-focusVisible": {
              outline: "2px solid var(--border-focus)",
              outlineOffset: -2,
              boxShadow: "inset 0 0 0 1px var(--border-focus)",
            },
          }),
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: 36,
            height: 36,
            color: "var(--border-input)",
            "&.Mui-checked": { color: theme.palette.primary.main },
            "&.Mui-focusVisible": { outline: "2px solid var(--border-focus)", outlineOffset: -2 },
          }),
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: 344,
            minHeight: 48,
            borderRadius: 4,
            backgroundColor: theme.palette.primary.dark,
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          grouped: {
            margin: 0,
            "&:not(:first-of-type)": {
            borderRadius: "var(--button-radius)",
            },
            "&:first-of-type": {
            borderRadius: "var(--button-radius)",
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: "var(--button-height)",
            minHeight: "var(--button-height)",
            padding: "10px 16px",
            borderRadius: "var(--button-radius)",
            border: "1px solid var(--border-default)",
            color: theme.palette.text.secondary,
            backgroundColor: "var(--surface-default)",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "var(--surface-hover)",
            },
            "&.Mui-selected, &.Mui-selected:hover": {
              color: "var(--text-primary)",
              background: "var(--surface-hover)",
              borderColor: "var(--border-default)",
              boxShadow: "none",
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "4px !important",
            "&::before": {
              display: "none",
            },
          }),
        },
      },
    },
  });
};
